import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

type SelectOption = { label: string; value: number };
type ProjectRecord = { id: number; project_name?: string; domain?: string };

@Component({
  selector: 'app-add-srv',

  templateUrl: './add-srv.component.html',
  styleUrl: './add-srv.component.css'
})
export class AddSRVComponent implements OnInit {
  sivFrom!: FormGroup;
  selectedDomain = 'Residential'; // default
  domainOptions:any = [];
  projectSelectOptions: SelectOption[] = [];
  projectRecords: ProjectRecord[] = [];
  staffSelectOptions: SelectOption[] = [];
  optionsMap: { [key: string]: any[] } = {
    domainOptionsDrp: this.domainOptions,
    projectOptions: this.projectSelectOptions,
    staffOptions: this.staffSelectOptions,
  };

  domainFields: { [key: string]: any[] } = {
    Residential: [
      { key: "tilePalletsReceived", "label": "Tile Pallets Received", "type": "number", "required": false, "value": "" },
      { key: "paintDrums", "label": "Paint Drums", "type": "text", "required": false, "value": "" },
    ],
    Commercial: [
      { key: "hvacUnitsReceived", "label": "HVAC Units Received", "type": "number", "required": false, "value": "" },
      { key: "fireSafetyGear", "label": "Fire Safety Gear", "type": "text", "required": false, "value": "" },
    ],
    Educational: [
      { key: "labEquipmentBoxes", "label": "Lab Equipment Boxes", "type": "number", "required": false, "value": "" },
      { key: "sportsEquipment", "label": "Sports Equipment", "type": "text", "required": false, "value": "" },
    ],
    Water: [
      { key: "pipeSections", "label": "Pipe Sections", "type": "number", "required": false, "value": "" },
      { key: "chlorinationUnits", "label": "Chlorination Units", "type": "number", "required": false, "value": "" },
    ],
    Power: [
      { key: "cableDrums", "label": "Cable Drums", "type": "number", "required": false, "value": "" },
      { key: "transformerOilDrums", "label": "Transformer Oil Drums", "type": "text", "required": false, "value": "" },
    ],
    Gas: [
      { key: "pressureRegulators", "label": "Pressure Regulators", "type": "number", "required": false, "value": "" },
      { key: "safetyValves", "label": "Safety Valves", "type": "number", "required": false, "value": "" },
    ],
    Telecom: [
      { key: "fiberCableSpools", "label": "Fiber Cable Spools", "type": "number", "required": false, "value": "" },
      { key: "antennaUnits", "label": "Antenna Units", "type": "number", "required": false, "value": "" },
    ],
    Road: [
      { key: "asphaltDelivered", "label": "Asphalt Delivered", "type": "text", "required": false, "value": "" },
      { key: "roadSignageBoards", "label": "Road Signage Boards", "type": "number", "required": false, "value": "" },
    ],
    Consultancy: [
      { key: "surveyEquipment", "label": "Survey Equipment", "type": "text", "required": false, "value": "" },
      { key: "referenceMaterials", "label": "Reference Materials", "type": "text", "required": false, "value": "" },
    ],
  };


  commonFields = [
    { key: "receiptVoucherId", "label": "Receipt Voucher ID", "type": "text", "required": true, "value": "" },
    { key: "project_common_id", label: "Project Name", type: "select", optionKey: "projectOptions", required: true, value: "", onChange: (value: any) => this.onProjectSelected(value) },
    { key: "receiptDate", "label": "Receipt Date", "type": "date", "required": true, "value": "" },
    { key: "receivedFrom", "label": "Received From", "type": "text", "required": true, "value": "" },
    { key: "itemDescription", "label": "Item Description", "type": "text", "required": true, "value": "" },
    { key: "quantityReceived", "label": "Quantity Received", "type": "number", "required": true, "value": "" },
    { key: "unitOfMeasure", "label": "Unit of Measure", "type": "text", "required": true, "value": "" },
    { key: "storeLocation", "label": "Store Location", "type": "text", "required": true, "value": "" },
    { key: "authorizedByStaffId", "label": "Authorized By", "type": "select", "optionKey": "staffOptions", "required": true, "value": "" },
    { key: "inspectedByStaffId", "label": "Inspected By", "type": "select", "optionKey": "staffOptions", "required": true, "value": "" },
    { key: "remarks", "label": "Remarks", "type": "text", "required": false, "value": "" },
    { key: 'domain', label: 'Domain', type: 'select', optionKey: 'domainOptionsDrp', required: true, value: 'Residential', onChange: (value: string) => this.onDomainChange(value) }
  ];

  pickerMap: { [key: string]: MatDatepicker<any> } = {};
  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly toastrMsgService: ToastrMsgService,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.domainOptions = [];
    this.optionsMap['domainOptionsDrp'] = this.domainOptions;
    this.sivFrom = this.fb.group({});
    this.commonFields.forEach(field => {
      const validators = field.required ? [Validators.required] : [];
      this.sivFrom.addControl(field.key, this.fb.control(field.value || '', validators));
    });
    this.onDomainChange(this.selectedDomain);
    this.loadLookups();
  }

  private staffLabel(s: Record<string, unknown>): string {
    const fn = [s['first_name'], s['last_name']].filter(Boolean).join(' ').trim();
    const full = String(s['full_name'] || '').trim();
    if (full) return full;
    if (fn) return fn;
    const id = s['id'] ?? s['personnel_id'];
    return id != null ? `Staff #${id}` : '';
  }

  private loadLookups(): void {
    forkJoin({
      projects: this.apiService.getProjectListForDropdown({ limit: 200 }),
      staff: this.apiService.getStaff({ keyword: '' }),
    }).subscribe({
      next: ({ projects, staff }) => {
        const projectRows = (projects as { data?: Record<string, unknown>[] })?.data ?? [];
        const staffRows = (staff as { data?: Record<string, unknown>[] })?.data ?? [];
        this.projectRecords = projectRows.map((p) => ({
          id: Number(p['id']),
          project_name: String(p['project_name'] || ''),
          domain: p['domain'] != null ? String(p['domain']) : '',
        }));
        this.projectSelectOptions = projectRows.map((p) => ({
          label: String(p['project_name'] || `Project #${p['id']}`),
          value: Number(p['id']),
        }));
        this.staffSelectOptions = staffRows
          .map((s: Record<string, unknown>) => {
            const id = Number(s['id'] ?? s['personnel_id']);
            return { label: this.staffLabel(s), value: id };
          })
          .filter((o: SelectOption) => Number.isFinite(o.value));
        this.optionsMap['projectOptions'] = this.projectSelectOptions;
        this.optionsMap['staffOptions'] = this.staffSelectOptions;
        this.buildDomainOptionsFromProjects();
      },
      error: () => {
        this.toastrMsgService.error('Failed to load projects or staff');
      },
    });
  }

  private resolveProjectName(id: unknown): string {
    const n = Number(id);
    if (!Number.isFinite(n)) return '';
    return this.projectSelectOptions.find((o) => o.value === n)?.label || '';
  }

  private resolveStaffName(id: unknown): string {
    const n = Number(id);
    if (!Number.isFinite(n)) return '';
    return this.staffSelectOptions.find((o) => o.value === n)?.label || '';
  }

  private normalizePayload(raw: Record<string, unknown>): Record<string, unknown> {
    const payload = { ...raw };
    const authId = payload['authorizedByStaffId'];
    const inspId = payload['inspectedByStaffId'];
    payload['projectName'] = this.resolveProjectName(payload['project_common_id']);
    payload['authorizedBy'] = this.resolveStaffName(authId);
    payload['inspectedBy'] = this.resolveStaffName(inspId);
    delete payload['project_common_id'];
    delete payload['authorizedByStaffId'];
    delete payload['inspectedByStaffId'];
    if (Number.isFinite(Number(authId))) {
      payload['authorized_by_staff_id'] = Number(authId);
    }
    if (Number.isFinite(Number(inspId))) {
      payload['inspected_by_staff_id'] = Number(inspId);
    }
    const rd = payload['receiptDate'];
    if (rd instanceof Date) {
      payload['receiptDate'] = (rd as Date).toISOString().split('T')[0];
    }
    return payload;
  }

  onSubmit(): void {
    if (this.sivFrom.invalid) {
      this.sivFrom.markAllAsTouched();
      this.toastrMsgService.error('Please fill all required fields');
      return;
    }
    const raw = this.sivFrom.getRawValue() as Record<string, unknown>;
    const pidRaw = raw['project_common_id'];
    const project_common_id =
      pidRaw != null && pidRaw !== '' ? Number(pidRaw) : NaN;
    const payload = this.normalizePayload(raw);
    this.apiService.createSrvSivRequest({
      type: 'SRV',
      project_common_id: Number.isFinite(project_common_id) ? project_common_id : null,
      payload,
    }).subscribe({
      next: () => {
        this.toastrMsgService.success('SRV saved successfully');
        void this.router.navigate(['/home/srv-list']);
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Save failed';
        this.toastrMsgService.error(msg);
      },
    });
  }
  onReset(): void {
    this.sivFrom.reset();
  }

  /**
   * Build the Domain dropdown from the unique domains found across the
   * loaded projects. Each option's value is normalized to the matching
   * `domainFields` key (case-insensitive) when one exists, so that
   * domain-specific fields can still render. If the currently selected
   * domain isn't part of the new list, snap it to the first option.
   */
  private buildDomainOptionsFromProjects(): void {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    for (const p of this.projectRecords) {
      const raw = String(p.domain || '').trim();
      if (!raw) continue;
      const matched = Object.keys(this.domainFields).find(
        (k) => k.toLowerCase() === raw.toLowerCase()
      );
      const value = matched || raw;
      if (seen.has(value)) continue;
      seen.add(value);
      opts.push({ value, label: value });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    this.domainOptions = opts;
    this.optionsMap['domainOptionsDrp'] = this.domainOptions;

    if (opts.length === 0) return;
    const ctrl = this.sivFrom?.get('domain');
    const currentVal = ctrl?.value;
    if (!opts.some((o) => o.value === currentVal)) {
      const next = opts[0].value;
      ctrl?.setValue(next);
      this.onDomainChange(next);
    }
  }

  /**
   * When the user selects a project, look up its domain and auto-set the
   * Domain dropdown so the right domain-specific fields render. Falls back
   * silently if the project's domain is unknown to this form.
   */
  onProjectSelected(projectId: any): void {
    const id = Number(projectId);
    if (!Number.isFinite(id)) return;
    const proj = this.projectRecords.find((p) => p.id === id);
    const projDomain = String(proj?.domain || '').trim();
    if (!projDomain) return;
    const matchedKey = Object.keys(this.domainFields).find(
      (k) => k.toLowerCase() === projDomain.toLowerCase()
    );
    if (!matchedKey) return;
    if (this.sivFrom?.get('domain')?.value === matchedKey) return;
    this.sivFrom?.get('domain')?.setValue(matchedKey);
    this.onDomainChange(matchedKey);
  }

  onDomainChange(domain: string) {
    this.selectedDomain = domain;
    Object.keys(this.sivFrom.controls).forEach(key => {
      if (key !== 'domain' && this.domainFields[domain]?.find(field => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some(field => field.key === key)) {
        this.sivFrom.removeControl(key);
      }
    });

    this.domainFields[domain].forEach(field => {
      this.sivFrom.addControl(field.key, this.fb.control(field.value));
    });

  }

}